import * as d3 from 'd3';
import * as _ from 'lodash';

import { AugmentedLog, Log } from '../../../models';
import { colorScale } from '../shared/logsColorScale';
import { greyTextColor, whiteBackgroundColor } from '../../../common/colors';

export class ByApplicationChart {
    private _dispatch = d3.dispatch('click');
    private _groupBy: (d: AugmentedLog) => string;
    private _height: number;
    private _plotHeight: number = 50;
    private _titleHeight = 20;
    private _margin = { top: this._titleHeight + 15, left: 30, bottom: 30, right: 30 };
    private _paneHeight = this._plotHeight + this._margin.top + this._margin.bottom;
    private _plotWidth: number;
    private _svg: d3.Selection<any>;
    private _width: number;
    private _xAxis: d3.svg.Axis;
    private _xScale: d3.time.Scale<number, number>;
    private _yScale = d3.scale.ordinal<string, number>().domain(['DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL']);

    constructor(selector: string) {
        const selection = d3.select(selector);
        const clientRect = (selection
            .node() as any)
            .getBoundingClientRect();
        this._svg = selection.append('svg')
            .attr('width', clientRect.width)
            .attr('height', clientRect.height);
        this._width = clientRect.width;
        this._height = clientRect.height;
        this._plotWidth = this._width - this._margin.left - this._margin.right;
        this._yScale.rangeBands([this._plotHeight, 0], 0.5);
        this._xScale = d3.time.scale().range([0, this._plotWidth]);
        this._xAxis = d3.svg.axis().scale(this._xScale);
        this.updateCircles = this.updateCircles.bind(this);
    }

    groupBy(value: (d: AugmentedLog) => string): this {
        if (value) {
            this._groupBy = value;
        }
        return this;
    }

    size(value: { width: number, height: number }): this {
        this._width = value.width;
        this._height = value.height;
        this._svg.attr('width', this._width);
        this._plotWidth = this._width - this._margin.left - this._margin.right;
        this._xScale.range([0, this._plotWidth]);
        const applications = this._svg
            .selectAll('.application');
        applications.select('rect')
            .transition()
            .attr('width', this._width);
        applications.select('g.title')
            .transition()
            .attr('transform', `translate(${this._margin.left + this._plotWidth / 2},${this._titleHeight})`);
        const plot = applications.select('g.plot');
        plot.select('rect')
            .transition()
            .attr('width', this._plotWidth);
        const circleBound = plot.selectAll('.circle')
            .transition()
            .attr('cx', (d: AugmentedLog) => {
                return this._xScale(d.moment.toDate());
            });
        applications.select('g.x.axis')
            .transition()
            .call(this._xAxis);
        return this;
    }

    update(data: AugmentedLog[]) {
        const byApp: {
            key: string;
            values: {
                key: string;
                values: { key: string; values: AugmentedLog[] }[];
            }[];
        }[] = _.sortBy(d3.nest<Log>()
            .key(this._groupBy)
            .entries(data), d => d.key);
        this._svg.attr('height', this._paneHeight * byApp.length);
        this._xScale
            .domain(d3.extent(data, (d: AugmentedLog) => d.moment.toDate()))
            .nice();
        const dataBound = this._svg
            .selectAll('.application')
            .data(byApp);
        dataBound
            .exit()
            .remove();
        const self = this;
        const enterSelection = dataBound
            .enter()
            .append('g')
            .classed('application', true)
            .attr('transform', (d, i) => `translate(${0},${i * this._paneHeight})`)
            .style('pointer-events', 'all')
            // .style('cursor', 'pointer')
            .on('click', self._dispatch.click);
        enterSelection.append('rect')
            .attr('width', this._width)
            .attr('height', this._paneHeight)
            .style('fill', 'transparent');
        enterSelection
            .append('g')
            .classed('title', true)
            .attr('transform', `translate(${this._margin.left + this._plotWidth / 2},${this._titleHeight})`)
            .append('text')
            .style('text-anchor', 'middle');
        dataBound.select('g.title')
            .select('text').text(d => d.key);
        const plot = enterSelection.append('g')
            .classed('plot', true)
            .attr('transform', `translate(${this._margin.left},${this._margin.top})`);
        plot.append('rect')
            .attr('width', this._plotWidth)
            .attr('height', this._plotHeight)
            .style('fill', whiteBackgroundColor)
            .style('stroke', greyTextColor);
        plot.append('g')
            .classed('x axis', true)
            .attr('transform', `translate(${0},${this._plotHeight})`);
        dataBound.select('g.x.axis').call(this._xAxis);
        dataBound.selectAll('g.plot')
            .call(this.updateCircles);
    }

    updateCircles(plot: d3.Selection<any>) {
        const xScale = this._xScale;
        const update = plot
            .selectAll('.values')
            .data(d => d3.nest()
                .key((dd: AugmentedLog) => dd.level)
                .entries(d.values));
        update
            .exit()
            .remove();
        update
            .enter()
            .append('g')
            .classed('values', true);
        update.attr('transform', (d, i) => `translate(0, ${this._yScale(d.key)})`);
        const circleBound = update
            .selectAll('.circle')
            .data((d: { key: string; values: AugmentedLog[] }) => {
                return d.values;
            });
        circleBound.exit()
            .remove();
        circleBound
            .enter()
            .append('circle')
            .classed('circle', true)
            .attr('r', 2);
        circleBound
            .attr('cx', (d: AugmentedLog) => {
                return xScale(d.moment.toDate());
            })
            .attr('cy', 0)
            .style('fill', (d, i) => colorScale(d.level));
    }

    on(event: string, callback: (d, i) => void): this {
        this._dispatch.on(event, callback);
        return this;
    }
}